import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import {
  AdminApiConnectionCreation,
  AdminApiConnectionUpdate,
  AdminOrganizationDetails,
  AdminUserRetrieve,
  OrganizationPermissionsEnum,
} from 'api/models';
import BoxedContent from 'src/ui/molecules/boxed-content';
import CardItem from 'src/ui/atoms/card-item';
import DateAtom from 'src/ui/atoms/date';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import EmailResentSuccess from './email-resent-success';
import Link from 'core/routing/link';
import Tooltip from 'src/ui/atoms/tooltip';
import StatusTag from 'src/ui/atoms/status-tag';
import UseDeactivationModals from './deactivation-modals/use-deactivation-modals';
import DeactivateUserConfirmModal from './deactivation-modals/deactivate-user-confirm-modal';
import DeactivateUserSuccessModal from './deactivation-modals/deactivate-user-success-modal';
import ReactivateUserConfirmModal from './deactivation-modals/reactivate-user-confirm-modal';
import ReactivateUserSuccessModal from './deactivation-modals/reactivate-user-success-modal';
import IssuerUserApiConnectionsTable from './api-connections';
import Section from 'src/ui/atoms/section';
import Header from 'src/ui/atoms/header';
import AddApiConnectionModal from './add-api-connection-modal';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import {
  currentUserHasManageEnvironmentPermission,
  currentUserHasManageUsersAndGroupsPermission,
} from '../../helpers/userPermissions';
import AddApiConnectionSuccessModal from './add-api-connection-success-modal';
import {
  currentOrganizationHasCanUseIssuerApiPermission,
  currentOrganizationHasManageOrganizationPermission,
  currentOrganizationHasManageUsersAndGroupsPermission,
} from '../../helpers/organizationPermissions';
import DeleteApiConnectionSuccessModal from './delete-api-connection-success-modal';
import IssuerPermissionsTable from './permissions-table';
import AddPermissionModal from './add-permission-modal';
import DeletePermissionSuccessModal from './delete-permission-success-modal';
import UserGroupsTable from './user-groups-table';
import GroupPermissionsTable from '../group-details/permissions-table';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface IssuerUserDetailsPageProps {
  userId: string;
}

export interface DistributionPlatformDropdownOption {
  value: string;
  label: string;
}

const IssuerUserDetailsPage: FunctionComponent<IssuerUserDetailsPageProps> = ({ userId }) => {
  const [user, setUser] = useState<AdminUserRetrieve>();
  const [userOrganization, setUserOrganization] = useState<AdminOrganizationDetails | null>(null);
  const [canSeeUserPermissions, setCanSeeUserPermissions] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);
  const [hasIssuerApiConnection, setHasIssuerApiConnection] = useState(false);
  const [hasDistributorApiConnection, setHasDistributorApiConnection] = useState(false);
  const [isAddApiConnectionModalOpen, setAddApiConnectionModalOpen] = useState(false);
  const [isAddPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [distributionPlatforms, setDistributionPlatforms] = useState<DistributionPlatformDropdownOption[]>([]);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [createdApiConnection, setCreatedApiConnection] = useState<AdminApiConnectionCreation>();
  const [editedApiConnectionWithSecret, setEditedApiConnectionWithSecret] = useState<AdminApiConnectionUpdate>();
  const [deleteApiConnectionSuccessModalOpen, setDeleteApiConnectionSuccessModalOpen] = useState(false);
  const [isDeletePermissionSuccessModalOpen, setDeletePermissionSuccessModalOpen] = useState(false);

  const { currentUser } = useCurrentUserSelector();

  const { withApi, makeAuthenticatedApi } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const {
    isDeactivateUserConfirmModalOpen,
    isDeactivateUserSuccessModalOpen,
    isReactivateUserConfirmModalOpen,
    isReactivateUserSuccessModalOpen,
    setDeactivateUserConfirmModal,
    setDeactivateUserSuccessModal,
    setReactivateUserConfirmModal,
    setReactivateUserSuccessModal,
  } = UseDeactivationModals();

  const fetchUserDetails = useCallback(() => {
    withApi(async () => {
      const userDetails = await adminApi.adminUsersRetrieve({
        id: userId,
      });

      setUser(userDetails);
    });
  }, [userId]);

  const fetchUserOrganization = useCallback(() => {
    withApi(async () => {
      const organization = await adminApi.adminOrganizationsRetrieve({
        id: user?.organization.id!,
      });
      setUserOrganization(organization);
    });
  }, [user?.organization.id]);

  const resendEmail = useCallback(() => {
    withApi(async () => {
      await adminApi.adminUsersResendInvitationCreate({
        id: userId,
      });
      setEmailSent(true);
    });
  }, [userId, user?.email]);

  const deactivateUser = useCallback(() => {
    setDeactivateUserConfirmModal(true);
  }, []);

  const reactivateUser = useCallback(() => {
    setReactivateUserConfirmModal(true);
  }, []);

  useEffect(() => {
    if (user?.organization.id) {
      fetchUserOrganization();
    }
  }, [withApi, adminApi, user?.organization.id]);

  useEffect(() => {
    if (userOrganization) {
      setCanSeeUserPermissions(
        currentOrganizationHasManageOrganizationPermission(userOrganization) ||
          currentUserHasManageEnvironmentPermission(currentUser?.permissions || []),
      );
    }
  }, [JSON.stringify(userOrganization)]);

  useEffect(() => {
    fetchUserDetails();
  }, [withApi, adminApi, userId]);

  useEffect(() => {
    if (user) {
      getDistributionPlatforms();
    }
  }, [withApi, adminApi, user]);

  const canAddIssuerApiConnection = useMemo(() => {
    return user?.organization
      ? (user?.organization.hasScCore || user?.organization.hasScLaunchpad) &&
          currentOrganizationHasCanUseIssuerApiPermission(user?.organization)
      : false;
  }, [user?.organization]);

  const canAddDistributorApiConnection = useMemo(() => {
    return (
      (!!user?.organization.hasScBanking ||
        !!user?.organization.hasScDistributionHub ||
        !!user?.organization.hasScLaunchpad) &&
      !!distributionPlatforms?.length
    );
  }, [user?.organization, JSON.stringify(distributionPlatforms)]);

  const getDistributionPlatforms = useCallback(() => {
    withApi(async () => {
      try {
        const response = await adminApi.adminDistributionPlatformsList({
          hasFrontend: false,
          ...(currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || [])
            ? {}
            : { permissions: [OrganizationPermissionsEnum.MANAGE_ORGANIZATION_USERS_AND_GROUPS] }),
        });
        setDistributionPlatforms(
          response.results.map((item) => ({
            value: item.id,
            label: item.displayName,
          })),
        );
      } catch (e) {
        console.error(e);
      }
    });
  }, [adminApi, user, currentUser]);

  const getStatusVariant = (isActive?: boolean, tosAccepted?: boolean) => {
    if (isActive) return 'success';
    if (tosAccepted) return 'danger';
    return 'warning';
  };

  const getStatusLabel = (isActive?: boolean, tosAccepted?: boolean) => {
    if (isActive) return 'issuerPermissionsUsersTable.active';
    if (tosAccepted) return 'issuerPermissionsUsersTable.deactivated';
    return 'issuerPermissionsUsersTable.invitationPending';
  };

  if (!user) return <LoadingRing />;

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <Section>
        <WideContent
          title={<Translate name="issuerPermissionsUserDetails.title" args={[user.email]} />}
          titleSpacing="larger"
        >
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
            <CardItem title={<Translate name="issuerPermissionsUserDetails.userId" />} content={user.id} />
            <CardItem
              title={<Translate name="issuerPermissionsUserDetails.createdAt" />}
              content={<DateAtom>{user.createdAt}</DateAtom>}
            />
            <CardItem title={<Translate name="issuerPermissionsUserDetails.email" />} content={user.email} />
            <CardItem
              title={<Translate name="issuerPermissionsUserDetails.organization" />}
              content={user.organization?.name}
            />
            <CardItem
              title={<Translate name="issuerPermissionsUserDetails.lastLoginAt" />}
              content={user.lastLogin && <DateAtom showTime>{user.lastLogin}</DateAtom>}
            />
            <CardItem
              title={<Translate name="issuerPermissionsUserDetails.status" />}
              content={
                <StatusTag variant={getStatusVariant(user.isActive, user.tosAccepted)} truncate={true}>
                  <Translate name={getStatusLabel(user.isActive, user.tosAccepted)} />
                </StatusTag>
              }
            />
            <ActionButtons spacingTop="regular">
              {user.isActive ? (
                <Tooltip maxWidth={188} content={<Translate name="issuerPermissionsUserDetails.userActiveTooltip" />}>
                  <Button variant="link" onClick={() => {}} disabled>
                    <Translate name="issuerPermissionsUserDetails.resendEmailButton" />
                  </Button>
                </Tooltip>
              ) : (
                <Button variant="link" onClick={resendEmail}>
                  <Translate name="issuerPermissionsUserDetails.resendEmailButton" />
                </Button>
              )}
              {((!user.isActive && user.tosAccepted) || user.isActive) && (
                <Button variant="secondary" onClick={user.isActive ? deactivateUser : reactivateUser}>
                  <Translate
                    name={`issuerPermissionsUserDetails.${user.isActive ? 'deactivateUser' : 'reactivateUser'}`}
                  />
                </Button>
              )}
            </ActionButtons>
          </BoxedContent>
        </WideContent>
      </Section>
      <Section>
        <WideContent>
          <Header size="small">
            <Translate name="issuerPermissionsGroupsTable.title" />
          </Header>
          <UserGroupsTable
            userId={userId}
            forceDataLoad={forceDataLoad}
            setForceDataLoad={setForceDataLoad}
            skipFilterUrlSync={true}
          />
        </WideContent>
      </Section>
      <Section>
        <WideContent
          headerPanel={
            (canAddIssuerApiConnection || canAddDistributorApiConnection) &&
            ((userOrganization && currentOrganizationHasManageUsersAndGroupsPermission(userOrganization)) ||
              currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || [])) ? (
              (hasIssuerApiConnection && hasDistributorApiConnection) ||
              (hasIssuerApiConnection && !canAddDistributorApiConnection) ||
              (hasDistributorApiConnection && !canAddIssuerApiConnection) ? (
                <Tooltip
                  content={<Translate name="issuerUserApiConnectionsTable.tooltipDisabledMaxConnectionsPermissions" />}
                >
                  <Button variant="primary" icon="plus" iconColor="inherit" disabled>
                    <Translate name="issuerUserApiConnectionsTable.addButton" />
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="primary"
                  icon="plus"
                  iconColor="inherit"
                  onClick={() => setAddApiConnectionModalOpen(true)}
                >
                  <Translate name="issuerUserApiConnectionsTable.addButton" />
                </Button>
              )
            ) : (
              <Tooltip content={<Translate name="issuerUserApiConnectionsTable.tooltipDisabledAddButtonPermissions" />}>
                <Button variant="primary" icon="plus" iconColor="inherit" disabled>
                  <Translate name="issuerUserApiConnectionsTable.addButton" />
                </Button>
              </Tooltip>
            )
          }
        >
          <Header size="small">
            <Translate name="issuerUserApiConnectionsTable.title" />
          </Header>
          <IssuerUserApiConnectionsTable
            userId={userId}
            forceDataLoad={forceDataLoad}
            distributionPlatforms={distributionPlatforms}
            setForceDataLoad={setForceDataLoad}
            setHasIssuerApiConnection={setHasIssuerApiConnection}
            setHasDistributorApiConnection={setHasDistributorApiConnection}
            setEditedApiConnectionWithSecret={setEditedApiConnectionWithSecret}
            setDeleteApiConnectionSuccessModalOpen={setDeleteApiConnectionSuccessModalOpen}
            skipFilterUrlSync={true}
          />
        </WideContent>
      </Section>
      <Section>
        <WideContent
          headerPanel={
            ((userOrganization && currentOrganizationHasManageOrganizationPermission(userOrganization)) ||
              currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) && (
              <Button variant="primary" icon="plus" iconColor="inherit" onClick={() => setAddPermissionModalOpen(true)}>
                <Translate name="issuerPermissionsTable.addPermissionButton" />
              </Button>
            )
          }
        >
          <Header size="small">
            <Translate name="issuerPermissionsTable.title" />
          </Header>
          <IssuerPermissionsTable
            user={user}
            userId={userId}
            userOrganization={userOrganization}
            forceDataLoad={forceDataLoad}
            setForceDataLoad={setForceDataLoad}
            setDeletePermissionSuccessModalOpen={setDeletePermissionSuccessModalOpen}
            skipFilterUrlSync={true}
            hasPermission={canSeeUserPermissions}
          />
        </WideContent>
      </Section>
      <WideContent>
        <Header size="small">
          <Translate name="issuerUserGroupPermissionsTable.title" />
        </Header>
        <GroupPermissionsTable
          userId={userId}
          forceDataLoad={forceDataLoad}
          skipFilterUrlSync={true}
          hasPermission={canSeeUserPermissions}
          groupOrganization={userOrganization}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        <ActionButtons align="start">
          <Link icon="arrow-left" to="/dashboard/permissions">
            <Translate as="span" name="issuerPermissionsUserDetails.backToPermissions" />
          </Link>
        </ActionButtons>
      </WideContent>

      {emailSent && <EmailResentSuccess email={user.email} onClose={() => setEmailSent(false)} />}
      {isDeactivateUserConfirmModalOpen && (
        <DeactivateUserConfirmModal
          userId={userId}
          userName={user.email}
          onSuccess={() => {
            setDeactivateUserSuccessModal(true);
            fetchUserDetails();
          }}
          onClose={() => setDeactivateUserConfirmModal(false)}
        />
      )}
      {isDeactivateUserSuccessModalOpen && (
        <DeactivateUserSuccessModal onClose={() => setDeactivateUserSuccessModal(false)} />
      )}
      {isReactivateUserConfirmModalOpen && (
        <ReactivateUserConfirmModal
          userId={userId}
          userName={user.email}
          onSuccess={() => {
            setReactivateUserSuccessModal(true);
            fetchUserDetails();
          }}
          onClose={() => setReactivateUserConfirmModal(false)}
        />
      )}
      {isReactivateUserSuccessModalOpen && (
        <ReactivateUserSuccessModal onClose={() => setReactivateUserSuccessModal(false)} />
      )}
      {isAddApiConnectionModalOpen && (
        <AddApiConnectionModal
          onSaveApiConnection={() => {
            setForceDataLoad(true);
            setAddApiConnectionModalOpen(false);
          }}
          onClose={() => setAddApiConnectionModalOpen(false)}
          user={user}
          hasIssuerApiConnection={hasIssuerApiConnection}
          hasDistributorApiConnection={hasDistributorApiConnection}
          organizationId={user.organization?.id}
          setCreatedApiConnection={setCreatedApiConnection}
          canAddIssuerApiConnection={canAddIssuerApiConnection}
          canAddDistributorApiConnection={canAddDistributorApiConnection}
        />
      )}
      {createdApiConnection && (
        <AddApiConnectionSuccessModal
          onClose={() => setCreatedApiConnection(undefined)}
          createdApiConnection={createdApiConnection}
        />
      )}
      {editedApiConnectionWithSecret && (
        <AddApiConnectionSuccessModal
          onClose={() => setEditedApiConnectionWithSecret(undefined)}
          editedApiConnection={editedApiConnectionWithSecret}
        />
      )}
      {deleteApiConnectionSuccessModalOpen && (
        <DeleteApiConnectionSuccessModal onClose={() => setDeleteApiConnectionSuccessModalOpen(false)} />
      )}
      {isAddPermissionModalOpen && (
        <AddPermissionModal
          user={user}
          userOrganization={userOrganization}
          onAddUserPermission={() => {
            setForceDataLoad(true);
            setAddPermissionModalOpen(false);
          }}
          onClose={() => setAddPermissionModalOpen(false)}
        />
      )}
      {isDeletePermissionSuccessModalOpen && (
        <DeletePermissionSuccessModal onClose={() => setDeletePermissionSuccessModalOpen(false)} />
      )}
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerUserDetailsPage, AppType.ISSUER);
